import React, { useEffect } from 'react';
import Grid from '@mui/material/Grid';
import { useParams } from 'react-router-dom';
import { Box, Tab, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Breadcrumbs from 'components/atoms/Breadcrumbs';
import { ERouteLinks } from 'models/route';
import { StyledPageTitle } from 'theme/styles';
import Icon from 'components/atoms/Icon/Icon';
import { EIconTypes } from 'constants/Icons';
import { colors } from 'theme/colors';
import ApplicationStore from 'state/ApplicationStore';
import { useMyApplication } from 'pages/MyApplicationsPage/hooks';
import { StyledTabs } from './styles';
import ApplicationDetailsStep from './ApplicationDetailsStep';
import ConversationStep from './ConversationStep';
import PaymentStep from './PaymentStep';
import AppointmentStep from './AppointmentStep';
import DocumentsStep from './DocumentsStep';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const CustomTabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ paddingTop: 3 }}>{children}</Box>}
    </div>
  );
};

const a11yProps = (index: number) => ({
  id: `simple-tab-${index}`,
  'aria-controls': `simple-tabpanel-${index}`,
});

const ApplicationDetailsPage = () => {
  const { id = '' } = useParams<{ id: string }>();
  const [step, setStep] = React.useState(0);
  const { application, getApplicationDetails } = useMyApplication();
  const { t } = useTranslation();

  useEffect(() => {
    (async () => {
      await getApplicationDetails(id);
    })();
  }, []);

  useEffect(() => {
    if (application) {
      ApplicationStore.setFormData(JSON.parse(application.data));
    }
  }, [application]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setStep(newValue);
  };

  return (
    <>
      <Breadcrumbs
        items={[
          { label: 'My Applications', route: ERouteLinks.MyApplications },
          'Application Details',
        ]}
      />

      {application && (
        <Grid container>
          <Grid item xs={6}>
            <StyledPageTitle variant="h4">{application.name}</StyledPageTitle>
          </Grid>
          <Grid item xs={6} sx={{ textAlign: 'right' }}>
            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
              Application Status
            </Typography>
            <br />
            <Typography variant="caption">
              {t(`applicationStatus.${application.status}`)}
            </Typography>
          </Grid>

          <Box sx={{ width: '100%' }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <StyledTabs
                value={step}
                onChange={handleChange}
                aria-label="tabs"
                variant="fullWidth"
              >
                <Tab
                  label="Application Details"
                  icon={<Icon type={EIconTypes.applicationDetails} color={colors.blue600} />}
                  iconPosition="start"
                  {...a11yProps(0)}
                />
                <Tab
                  label="Documents"
                  icon={<Icon type={EIconTypes.applicationDocuments} color={colors.blue600} />}
                  iconPosition="start"
                  {...a11yProps(1)}
                />
                <Tab
                  label="Conversation"
                  icon={
                    <Icon type={EIconTypes.applicationConversation} color={colors.blue600} />
                  }
                  iconPosition="start"
                  {...a11yProps(2)}
                />
                <Tab
                  label="Payment"
                  icon={<Icon type={EIconTypes.applicationPayment} color={colors.blue600} />}
                  iconPosition="start"
                  {...a11yProps(3)}
                />
                <Tab
                  label="Appointment"
                  icon={
                    <Icon type={EIconTypes.applicationAppointment} color={colors.blue600} />
                  }
                  iconPosition="start"
                  {...a11yProps(4)}
                />
              </StyledTabs>
            </Box>

            <CustomTabPanel value={step} index={0}>
              <ApplicationDetailsStep />
            </CustomTabPanel>
            <CustomTabPanel value={step} index={1}>
              <DocumentsStep />
            </CustomTabPanel>
            <CustomTabPanel value={step} index={2}>
              <ConversationStep />
            </CustomTabPanel>
            <CustomTabPanel value={step} index={3}>
              <PaymentStep />
            </CustomTabPanel>
            <CustomTabPanel value={step} index={4}>
              <AppointmentStep />
            </CustomTabPanel>
          </Box>
        </Grid>
      )}
    </>
  );
};

export default ApplicationDetailsPage;
